import { useLayoutEffect, useRef, useState } from 'react'

import useSessionState from '@hooks/useSessionState'
import { useSearchParams } from 'react-router-dom'
import useLoading from '@hooks/useLoading'
import useDialog from '@hooks/useDialog'
import { Toast } from '@base-ui/react'
import axios from '@utils/webRequests'
import { size } from '@styles/theme'
import styled from '@emotion/styled'

import { UserMediaUploadButton } from './UserMediaUploadButton'
import { UserMediaSendButton } from './UserMediaSendButton'
import { MediaDrawerHeader } from './MediaDrawerHeader'
import { UserMediaViewer } from './UserMediaViewer'
import { SwipeableDrawer } from '@mui/material'
import { MediaGridItem } from './MediaGridItem'

const drawerHeight = '85dvh'
const tabHeight = size(0.5)
const tabMargin = size(2) // *2 for top and bottom
const tabTotalHeight = size(4.5)
const drawerHeaderHeight = size(6)

const drawerDimensions = {
  drawerHeight,
  tabHeight,
  tabMargin,
  tabTotalHeight,
  drawerHeaderHeight,
}

const DrawerTab = styled.div`
  width: ${size(6)};
  height: ${tabHeight};
  border-radius: 100px;
  background-color: grey;
  margin: ${tabMargin} auto ${tabMargin} auto;
  flex-shrink: 0;
`

const DrawerContents = styled.div`
  position: relative;
  padding: 0 ${size(1.5)};
  height: calc(${drawerHeight} - ${tabTotalHeight});
  width: 100dvw;
  overflow: hidden;
`

const GridContents = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: auto; 
  gap: ${size(1)};
  overflow-y: scroll;
  overflow-x: hidden;
  width: calc(100dvw - ${size(3)}); // accounts for DrawerContents padding on left and right
  height: calc(100% - ${drawerHeaderHeight});
  padding-bottom: ${size(8)};
`

const slotProps = {
  paper: {
    style: {
      height: drawerHeight,
      width: '100dvw',
      borderRadius: `25px 25px 0 0`,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(10px)',
    }
  },
  backdrop: {
    style: {
      backgroundColor: 'rgba(255,255,255,0.1)'
    }
  }
}

const defaultSelectedState = {
  media: [],
  index: {},
}

const UserMediaDrawer = ({ isOpen, setClosed, handleConversationNewMessageSent, addNewMessageAfterSend }) => {
  const [isViewerOpen, openViewer, closeViewer] = useDialog('user-media-image-viewer', 'true')
  const [selected, setSelected] = useState(structuredClone(defaultSelectedState))
  const { isLoading, setIsLoading, isLoadingDelayed } = useLoading()
  const [cachedMedia, setCachedMedia] = useSessionState('user-media-drawer', [])
  const [cachedMediaIsLoaded, setCachedMediaIsLoaded] = useSessionState(false)
  const toastManager = Toast.useToastManager()
  const [searchParams] = useSearchParams()
  const drawerRef = useRef()
  const gridRef = useRef()
  const touchYStart = useRef(0)
  
  const isMediaSelected = !!(selected?.media?.length > 0)
  const conversationId = searchParams.get('conversation_id')
  
  // gets cached images on initial render
  useLayoutEffect(() => {
    if (cachedMedia?.length > 0 || cachedMediaIsLoaded) return;
    axios.get(`api/my/images/cached_images`)
      .then((res) => {
        setCachedMedia(res.data)
        setCachedMediaIsLoaded(true)
      })
      .catch(console.log)
  }, [cachedMedia])

  const addSelected = (newMedia) => {
    if (selected.media.length > 5) {
      toastManager.add({ text: 'Select up to 6' })
      return
    }
    const newSelected = { ...selected }
    newSelected.media.push(newMedia)
    newSelected.index[newMedia?.uuid] = newSelected.media.length - 1
    setSelected(newSelected)
  }

  const removeSelected = (removedMedia) => {
    const newSelected = { ...selected }
    newSelected.media = newSelected.media.filter(media => media?.uuid !== removedMedia?.uuid)
    newSelected.index = newSelected.media.reduce((acc, item, i) => {
      acc[item?.uuid] = i
      return acc
    }, {})
    setSelected(newSelected)
  }

  const resetSelected = () =>
    setSelected(structuredClone(defaultSelectedState))

  const onUpdateSuccess = (newImage) => {
    if (newImage?.[0]) {
      setCachedMedia([newImage[0], ...cachedMedia])
      if (selected?.media?.length < 6) {
        // Add to selection after upload if max isn't already selected
        addSelected(newImage[0])
      }
    }
  }

  const handleCreate = () => {
    if (!conversationId || isLoading) return;
    setIsLoading(true)
    const idArray = selected.media.map((media) => media?.uuid)
    axios.post(`/api/my/conversations/${conversationId}/image_messages`, { cached_images: idArray } )
      .then((res) => {
        if (Array.isArray(res.data)) {
          const newMessages = res.data.toReversed()
          const lastMessage = newMessages[0]
          lastMessage.isMetaDataOpen = true
          addNewMessageAfterSend(newMessages)
          handleConversationNewMessageSent(lastMessage)
          resetSelected()
          toastManager.add({ text: 'sent images' })
        }
        setClosed()
      })
      .catch((err) => {
        console.log(err)
        toastManager.add({ text: 'failed to send images' })
      })
      .finally(() => setIsLoading(false))
  }

  const handleDelete = () => {
    if (isLoading) return;
    setIsLoading(true)
    const idArray = selected.media.map((media) => media?.uuid)
    axios.delete(`api/my/images/cached_images`, { params: { cached_images: idArray } })
      .then(() => {
        const newCachedMedia = cachedMedia.filter((media) => !(media?.uuid in selected.index) )
        setCachedMedia(newCachedMedia)
        resetSelected()
        toastManager.add({ text: 'successfully deleted' })
      })
      .catch((err) => {
        console.log(err)
        toastManager.add({ text: 'failed to delete' })
      })
      .finally(() => setIsLoading(false))
  }

  const renderCachedMedia = () => {
    return cachedMedia?.map?.((img, i) => (
      <MediaGridItem
        openViewer={openViewer}
        disabled={isLoading}
        removeSelected={removeSelected}
        addSelected={addSelected}
        selectedState={selected}
        image={img}
        key={img.uuid || i}
      />
    ))
  }

  const renderFallbackMedia = () => {
    const imageMax = 35 - cachedMedia?.length || 0;
    const fallbackArr = []
    for(let i = 0; i < imageMax; i++) {
      fallbackArr.push(<div key={i} style={{ backgroundColor: 'rgba(50,50,50,0.5)', borderRadius: 12, aspectRatio: 1 }} />)
    }
    return fallbackArr
  }

  const handleDetermineIfGridScrolls = (e) => {
    if (
      // grid should scroll down if it's not already scrolled to top
      gridRef?.current?.scrollTop !== 0 ||
      // if current clientY is less than the start client y, grid is scrolling up (which is always allowed)
      (e.touches[0].clientY < touchYStart?.current)
    ) {
      e.stopPropagation()
    }
  }

  return (
    <SwipeableDrawer
      disableSwipeToOpen
      onOpen={() => {}}
      ref={drawerRef}
      anchor='bottom'
      slotProps={slotProps}
      open={isOpen}
      onClose={setClosed}>
      <DrawerTab />
      <DrawerContents>
        {isViewerOpen &&
          <UserMediaViewer
            isOpen={isViewerOpen}
            selectedState={selected}
            removeSelected={removeSelected}
            addSelected={addSelected}
          />
        }
        <MediaDrawerHeader
          isLoading={isLoading}
          isLoadingDelayed={isLoadingDelayed}
          drawerDimensions={drawerDimensions}
          isMediaSelected={isMediaSelected}
          setClosed={setClosed}
          deleteMedia={handleDelete}
          hasMedia={cachedMedia?.length > 0}
        />
        <GridContents
          ref={gridRef}
          onTouchStart={e => touchYStart.current = e.touches[0]?.clientY}
          onTouchMove={handleDetermineIfGridScrolls}>
          <UserMediaUploadButton
            onUpdateSuccess={onUpdateSuccess}
            disabled={isLoading}
            loaderRef={drawerRef}
          />
          {renderCachedMedia()}
          {renderFallbackMedia()}
        </GridContents>
        <UserMediaSendButton
          isMediaSelected={isMediaSelected}
          disabled={isLoading}
          onClick={handleCreate}
        />
      </DrawerContents>
    </SwipeableDrawer>
  )
}

export { UserMediaDrawer, drawerDimensions }
